package com.iregained.eventapp.components.sections.event_booking.sections

import androidx.compose.runtime.*
import com.iregained.eventapp.components.models.TimeSlotData
import com.iregained.eventapp.components.models.availableSpots
import com.iregained.eventapp.components.models.isFull
import com.iregained.eventapp.components.sections.event_booking.styles.CardContainerStyle
import com.iregained.eventapp.components.sections.event_booking.styles.DateTabStyle
import com.iregained.eventapp.components.sections.event_booking.styles.SlotPillStyle
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.utils.LocalBreakpoint
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

@Composable
fun EventSlotPickerSection(
    availableSlots: List<TimeSlotData>, selectedSlot: TimeSlotData?, onSlotSelected: (TimeSlotData?) -> Unit
) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    Column(
        modifier = CardContainerStyle.toModifier().backgroundColor(palette.custom.cardBackground)
            .margin(topBottom = 24.px), horizontalAlignment = Alignment.Start
    ) {
        SpanText(
            text = "1. Select Evaluation Session Time",
            modifier = Modifier.fontSize(20.px).fontWeight(FontWeight.Bold).margin(bottom = 16.px)
        )

        if (availableSlots.isEmpty()) {
            SpanText(
                text = "No booking slots are currently available for this event.",
                modifier = Modifier.color(palette.custom.darkText).fontSize(16.px)
            )
            return@Column
        }

        val uniqueDates = remember(availableSlots) { availableSlots.map { it.date }.distinct() }
        var activeDate by remember(uniqueDates) { mutableStateOf(uniqueDates.firstOrNull() ?: "") }

        // Date Tabs (Converted to SimpleGrid for mobile responsiveness)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .gap(8.px)
                .margin(bottom = 20.px)
                .flexWrap(FlexWrap.Wrap) // Forces items to a new line if the screen is too small
                .justifyContent(JustifyContent.Center)  // Perfectly centers the tabs on every line
        ) {
            uniqueDates.forEach { dateStr ->
                val isSelected = dateStr == activeDate
                Box(
                    modifier = DateTabStyle.toModifier()
                        .padding(
                            topBottom = 8.px, leftRight = 12.px
                        ) // Reduced horizontal padding to prevent text overflow
                        .borderRadius(4.px)
                        .backgroundColor(if (isSelected) palette.custom.brandPrimary else palette.custom.neutralLight)
                        .cursor(Cursor.Pointer).onClick {
                            if (activeDate != dateStr) {
                                activeDate = dateStr
                                onSlotSelected(null)
                            }
                        }, contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = dateStr,
                        modifier = Modifier.color(if (isSelected) org.jetbrains.compose.web.css.Color.white else palette.custom.darkText)
                            .fontWeight(FontWeight.Medium).textAlign(TextAlign.Center)
                    )
                }
            }
        }

        // Time Slots Grid
        val slotsForDate = availableSlots.filter { it.date == activeDate }
        SimpleGrid(
            numColumns = numColumns(1, sm = 2, md = 3), modifier = Modifier.fillMaxWidth()
        ) {
            slotsForDate.forEach { slot ->
                val isSelected = selectedSlot?.slotId == slot.slotId
                val isFull = slot.isFull()

                Box(
                    modifier = SlotPillStyle.toModifier().margin(8.px).backgroundColor(
                        when {
                            isFull -> palette.custom.neutralDisabled
                            isSelected -> palette.custom.brandPrimary
                            else -> palette.custom.surfaceBackground
                        }
                    ).border(
                        width = 2.px,
                        style = LineStyle.Solid,
                        color = if (isSelected) palette.custom.brandPrimary else palette.custom.neutralLight
                    ).cursor(if (isFull) Cursor.NotAllowed else Cursor.Pointer)
                        .onClick { if (!isFull) onSlotSelected(slot) }, contentAlignment = Alignment.Center
                ) {
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        SpanText(
                            text = "${slot.startTime} - ${slot.endTime}",
                            modifier = Modifier.fontSize(16.px).fontWeight(FontWeight.Bold)
                                .color(if (isSelected) palette.custom.white else palette.custom.darkText)
                        )
                        SpanText(
                            text = if (isFull) "FULL" else "${slot.availableSpots()} spots left",
                            modifier = Modifier.fontSize(12.px).margin(top = 4.px)
                                .color(if (isSelected) palette.custom.white else palette.custom.darkText)
                        )
                    }
                }
            }
        }
    }
}